'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/components/CartContext';

const SHIPPING_THRESHOLD = 75;
const SHIPPING_COST = 8.95;

export default function CartClient() {
  const { items, itemCount, subtotal, removeItem, updateQuantity } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');

  const discount = promoApplied ? subtotal * 0.15 : 0;
  const shippingFree = subtotal - discount >= SHIPPING_THRESHOLD;
  const shipping = shippingFree ? 0 : SHIPPING_COST;
  const total = subtotal - discount + shipping;

  const handlePromo = () => {
    if (promoCode?.toUpperCase() === 'LUXE15') {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoError('Invalid promo code. Try LUXE15 for 15% off.');
    }
  };

  const amountToFreeShipping = Math.max(0, SHIPPING_THRESHOLD - (subtotal - discount));

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Header */}
      <div className="border-b border-[rgba(201,169,110,0.12)]">
        <div className="max-w-8xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-px bg-[#C9A96E]" />
            <span className="label-gold">
              {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
            </span>
          </div>
          <h1
            className="font-fraunces text-[#1A1612] leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 400 }}
          >
            Your <em className="gradient-gold not-italic">Cart</em>
          </h1>
        </div>
      </div>
      {/* Free Shipping Progress */}
      {items?.length > 0 && (
        <div
          className="border-b border-[rgba(201,169,110,0.12)]"
          style={{ background: 'rgba(201,169,110,0.05)' }}
        >
          <div className="max-w-8xl mx-auto px-6 lg:px-10 py-4">
            {shippingFree ? (
              <div className="flex items-center gap-2">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#C9A96E]" />
                <p className="text-[12px] font-semibold text-[#1A1612]">
                  You&apos;ve unlocked <span className="text-[#C9A96E]">free shipping</span>! 🎉
                </p>
              </div>
            ) : (
              <div>
                <p className="text-[12px] text-[#8A7E72] mb-2">
                  Add{' '}
                  <span className="font-semibold text-[#1A1612]">
                    ${amountToFreeShipping?.toFixed(2)}
                  </span>{' '}
                  more for free shipping
                </p>
                <div className="h-1 bg-[#E8D5B0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#C9A96E] rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, ((subtotal - discount) / SHIPPING_THRESHOLD) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="max-w-8xl mx-auto px-6 lg:px-10 py-10">
        {items?.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-24">
            <div
              className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              style={{
                background: 'rgba(201,169,110,0.08)',
                border: '1px solid rgba(201,169,110,0.2)',
              }}
            >
              <Icon name="ShoppingBagIcon" size={32} className="text-[#C9A96E]" />
            </div>
            <h2 className="font-fraunces text-[28px] font-light text-[#1A1612] mb-3">
              Your cart is empty
            </h2>
            <p className="text-[14px] text-[#8A7E72] mb-8">
              Discover our curated collection of premium beauty products.
            </p>
            <Link href="/products">
              <button className="btn-gold">Shop the Collection</button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-10 lg:gap-16">
            {/* Cart Items */}
            <div className="col-span-12 lg:col-span-7">
              <div className="space-y-0">
                {items?.map((item, index) => (
                  <div
                    key={`${item?.id}-${item?.variant}`}
                    className={`py-8 flex gap-5 ${
                      index < items?.length - 1 ? 'border-b border-[rgba(201,169,110,0.12)]' : ''
                    }`}
                  >
                    {/* Image */}
                    <Link href="/product-detail">
                      <div
                        className="flex-shrink-0 overflow-hidden bg-[#F0EBE0] cursor-pointer"
                        style={{ width: '100px', height: '130px' }}
                      >
                        <AppImage
                          src={item?.image}
                          alt={`${item?.name} in cart - ${item?.variant} variant`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <div>
                          <p className="label-gold mb-1" style={{ fontSize: '9px' }}>
                            {item?.category}
                          </p>
                          <Link href="/product-detail">
                            <h3 className="font-fraunces text-[17px] font-medium text-[#1A1612] hover:text-[#C9A96E] transition-colors cursor-pointer">
                              {item?.name}
                            </h3>
                          </Link>
                          {item?.variant && (
                            <p className="text-[12px] text-[#8A7E72] mt-0.5">{item?.variant}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(item?.id)}
                          className="text-[#B8AFA6] hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                          aria-label={`Remove ${item?.name} from cart`}
                        >
                          <Icon name="XMarkIcon" size={18} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        {/* Quantity */}
                        <div className="flex items-center border border-[rgba(26,22,18,0.12)]">
                          <button
                            onClick={() => updateQuantity(item?.id, item?.quantity - 1)}
                            className="qty-btn"
                            disabled={item?.quantity <= 1}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>
                          <span className="w-9 text-center text-[13px] font-semibold text-[#1A1612]">
                            {item?.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item?.id, item?.quantity + 1)}
                            className="qty-btn"
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="font-fraunces text-[18px] font-medium text-[#1A1612]">
                            ${(item?.price * item?.quantity)?.toFixed(2)}
                          </p>
                          {item?.quantity > 1 && (
                            <p className="text-[11px] text-[#8A7E72]">${item?.price} each</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Continue Shopping */}
              <div className="mt-8">
                <Link href="/products">
                  <button className="btn-outline flex items-center gap-2">
                    <Icon name="ArrowLeftIcon" size={14} />
                    Continue Shopping
                  </button>
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="col-span-12 lg:col-span-5">
              <div
                className="p-8 sticky top-28"
                style={{ background: 'white', border: '1px solid rgba(201,169,110,0.15)' }}
              >
                <h2 className="font-fraunces text-[22px] font-medium text-[#1A1612] mb-6">
                  Order Summary
                </h2>

                {/* Line Items */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-[#8A7E72]">Subtotal ({itemCount} items)</span>
                    <span className="font-medium text-[#1A1612]">${subtotal?.toFixed(2)}</span>
                  </div>

                  {promoApplied && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-[#C9A96E] flex items-center gap-1.5">
                        <Icon name="TagIcon" size={12} className="text-[#C9A96E]" />
                        LUXE15 (15% off)
                      </span>
                      <span className="text-[#C9A96E] font-medium">−${discount?.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-[13px]">
                    <span className="text-[#8A7E72]">Shipping</span>
                    <span
                      className={`font-medium ${shippingFree ? 'text-[#C9A96E]' : 'text-[#1A1612]'}`}
                    >
                      {shippingFree ? 'Free' : `$${SHIPPING_COST?.toFixed(2)}`}
                    </span>
                  </div>
                </div>

                <div className="divider-gold mb-5" />

                <div className="flex justify-between mb-6">
                  <span className="font-semibold text-[15px] text-[#1A1612]">Total</span>
                  <span className="font-fraunces text-[22px] font-medium text-[#1A1612]">
                    ${total?.toFixed(2)}
                  </span>
                </div>

                {/* Promo Code */}
                {!promoApplied ? (
                  <div className="mb-6">
                    <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                      Promo Code
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e?.target?.value?.toUpperCase())}
                        placeholder="Enter code"
                        className="input-luxe flex-1 text-[13px]"
                        onKeyDown={(e) => e?.key === 'Enter' && handlePromo()}
                      />
                      <button
                        onClick={handlePromo}
                        className="btn-outline px-4 py-0 text-[11px] whitespace-nowrap"
                      >
                        Apply
                      </button>
                    </div>
                    {promoError && <p className="text-[11px] text-red-400 mt-1.5">{promoError}</p>}
                    <p className="text-[10px] text-[#B8AFA6] mt-1.5">Try: LUXE15</p>
                  </div>
                ) : (
                  <div
                    className="mb-6 px-3 py-2.5 flex items-center justify-between"
                    style={{
                      background: 'rgba(201,169,110,0.08)',
                      border: '1px solid rgba(201,169,110,0.2)',
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name="CheckBadgeIcon" size={14} className="text-[#C9A96E]" />
                      <span className="text-[12px] font-semibold text-[#C9A96E]">
                        LUXE15 applied — 15% off!
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setPromoApplied(false);
                        setPromoCode('');
                      }}
                      className="text-[#8A7E72] hover:text-[#1A1612] text-[10px] underline"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* Checkout CTA */}
                <Link href="/checkout">
                  <button className="btn-gold w-full flex items-center justify-center gap-2 py-4">
                    <Icon name="LockClosedIcon" size={14} />
                    Proceed to Checkout
                  </button>
                </Link>

                {/* Trust */}
                <div className="mt-5 flex items-center justify-center gap-4">
                  {['SSL Secured', 'Free Returns', 'Clean Beauty']?.map((badge) => (
                    <div key={badge} className="flex items-center gap-1">
                      <Icon name="ShieldCheckIcon" size={11} className="text-[#C9A96E]" />
                      <span className="text-[10px] text-[#B8AFA6]">{badge}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
